/** English landing copy — default locale */
export const hero = {
  eyebrow: 'AI Trading Terminal · Multi-Asset',
  headline: ['Every market.', 'One terminal.'],
  subheadline:
    'Multi-timeframe signals, liquidation heatmaps, AI Market Brain, and forecast — in one fast web app. No tab-hopping, no API keys in the browser.',
  trustLine: 'No credit card · Free plan forever · Upgrade anytime',
  primaryCta: 'Get started free',
  secondaryCta: 'See how it works',
  secondaryAnchor: '#how-it-works',
};

export const trustStats = [
  { value: 14, suffix: '', label: 'Indicators' },
  { value: 4, suffix: '', label: 'Plan tiers' },
  { value: 8, suffix: '+', label: 'Data sources' },
  { value: 5, suffix: '', label: 'AI layers' },
];

export const dataProviders = [
  'Binance',
  'Bybit',
  'OKX',
  'Coinbase',
  'Kraken',
  'Yahoo Finance',
  'CoinGlass',
  'Etherscan',
];

export const productOverview = {
  eyebrow: 'What is MarketWatcher?',
  headline: ['Context over noise.', 'Signals with substance.'],
  description:
    'MarketWatcher is a browser-based trading terminal for crypto and equities. It combines technical analysis, orderflow data, and AI-assisted market insights in one unified interface — structured, transparent, and scaled by plan.',
  bullets: [
    'Multi-timeframe signals with confidence scores',
    'Server-side AI keys — secure, no browser exposure',
    'Liquidation heatmap & orderflow live in the chart',
    'Free plan with no time limit — start immediately',
  ],
};

export const howItWorks = {
  eyebrow: 'How it works',
  headline: ['Three steps.', 'One clear workflow.'],
  steps: [
    {
      num: '01',
      title: 'Watch the market',
      description:
        'Pick your tickers, set watchlists and alerts. Overview, charts, and news feed give you a single view of every market that matters.',
    },
    {
      num: '02',
      title: 'Analyze signals',
      description:
        'Multi-timeframe confluence, indicators, and AI Market Brain deliver structured insights — with confidence breakdown, not binary buy/sell.',
    },
    {
      num: '03',
      title: 'Decide with confidence',
      description:
        'Forecast, heatmap, and trade journal help you validate setups, document trades, and improve your process continuously.',
    },
  ],
};

export const whySection = {
  eyebrow: 'Why MarketWatcher?',
  headline: ['More context.', 'Better decisions.'],
  items: [
    {
      title: 'Context over noise',
      description:
        'Structured signals with confidence scores and MTF confirmation — not just red and green arrows.',
      tone: 'accent',
    },
    {
      title: 'Secure AI integration',
      description:
        'OpenAI, Anthropic, and Groq run through our backend proxy. Your API keys never touch the browser.',
      tone: 'success',
    },
    {
      title: 'Multi-asset in one place',
      description:
        'BTC, ETH, top alts, and equities via Yahoo Finance — one terminal for every market you trade.',
      tone: 'warn',
    },
    {
      title: 'Fair scaling',
      description:
        'From free entry to Terminal — pay only for the limits and features you actually need.',
      tone: 'accent',
    },
  ],
};

export const trustSecurity = {
  eyebrow: 'Trust & security',
  headline: ['Your keys stay', 'on our servers.'],
  description:
    'MarketWatcher is built for traders who care about data integrity and safe AI usage.',
  items: [
    {
      title: 'Server-side AI keys',
      description: 'LLM requests go through our backend proxy. No API keys in localStorage or browser extensions.',
    },
    {
      title: 'Plan-based quotas',
      description: 'Daily AI chat limits per plan prevent abuse and keep costs predictable for everyone.',
    },
    {
      title: 'HttpOnly session cookies',
      description: 'Authentication uses secure, server-managed sessions — not tokens in JavaScript.',
    },
    {
      title: 'Not financial advice',
      description: 'We provide market data and analysis tools. All trading decisions are yours. Capital is at risk.',
    },
  ],
};

export const useCases = {
  eyebrow: 'Use cases',
  headline: ['Built for every', 'trading style.'],
  personas: [
    {
      title: 'Swing trader',
      pain: 'Too many tabs, not enough multi-timeframe context.',
      solution: 'MTF matrix, overview center, and structured signals in one app.',
      plan: 'Core',
    },
    {
      title: 'Day trader',
      pain: 'Liquidation clusters and orderflow missing from standard charting.',
      solution: 'Heatmap overlay, live WS feed, and futures panel on Pro.',
      plan: 'Pro',
    },
    {
      title: 'Analyst / researcher',
      pain: 'AI insights without secure key management and quota control.',
      solution: 'AI Market Brain with server proxy and plan-based daily quotas.',
      plan: 'Pro',
    },
    {
      title: 'Teams & power users',
      pain: 'Single-monitor setup cannot support parallel workflows.',
      solution: 'Multi-monitor workspaces, portfolio tracking, and priority signals.',
      plan: 'Terminal',
    },
  ],
};

export const coreFeatures = {
  eyebrow: 'Core features',
  headline: ['Everything a modern', 'trader needs.'],
  description:
    'From multi-timeframe signals to liquidity heatmaps — without tab-hopping or API key chaos in the browser.',
  items: [
    {
      title: 'Multi-timeframe signals',
      description:
        'Tier-1 + tier-2 composite signals with MTF confirmation from 1m to 1d, including hit-rate smoother and narrative.',
      bullets: ['14 indicators', 'Pattern + structure', 'Adaptive weights'],
      emoji: '◇',
      tone: 'accent',
    },
    {
      title: 'AI Market Brain',
      description:
        'OpenAI / Anthropic / Groq via backend proxy — no API key in the browser. Plan-based daily quotas with rate limits.',
      bullets: ['Server-side keys', 'Plan quotas', 'Multi-provider routing'],
      emoji: '◈',
      tone: 'success',
    },
    {
      title: 'Liquidation heatmap',
      description:
        'CoinGlass + synthetic clusters + live WS feed. Visualizes magnetic price zones as chart overlay.',
      bullets: ['Live WS', 'Synthetic clusters', 'Chart overlay'],
      emoji: '◉',
      tone: 'warn',
    },
    {
      title: 'Forecast + CandlePredict',
      description:
        'Linreg forecast and AR(p) + Monte Carlo paths as OHLC synthesis — directly in the chart with confidence bands.',
      bullets: ['Linreg', 'Monte Carlo', 'OHLC synth'],
      emoji: '◬',
      tone: 'accent',
    },
    {
      title: 'Multi-asset pulse',
      description:
        'Overview center with pulse strip, MTF matrix, consensus score, community vote, event calendar, and news column.',
      bullets: ['Pulse strip', 'MTF matrix', 'Community vote'],
      emoji: '◐',
      tone: 'success',
    },
    {
      title: 'Wallet + portfolio',
      description:
        'EVM & BTC wallet integration, Etherscan & mempool reader, paper trading with long/short + leverage.',
      bullets: ['EVM + BTC', 'Long/short', 'Trade journal'],
      emoji: '◆',
      tone: 'danger',
    },
  ],
};

export const pricingSection = {
  eyebrow: 'Pricing',
  headline: ['Fair plans.', 'Clear limits.'],
  description:
    'From free entry to Terminal — switch plans anytime. No hidden fees.',
};

export const faqHome = [
  {
    q: 'Do I need an account to use MarketWatcher?',
    a: 'Yes. Registration is free and takes about 30 seconds. The Free plan includes basic charts and selected assets immediately — no credit card required.',
  },
  {
    q: 'What is included in the Free plan?',
    a: 'Basic charts, 3 tickers, 1 watchlist, 2 alerts, and a news feed. No time limit — upgrade to Core, Pro, or Terminal anytime.',
  },
  {
    q: 'How does AI Market Brain work?',
    a: 'AI requests run through our backend proxy. API keys stay on the server. Each plan has a daily AI chat quota (Core: 25, Pro: 100, Terminal: 400).',
  },
  {
    q: 'Can I upgrade or downgrade anytime?',
    a: 'Yes. Plan changes are available in your account at any time. The new plan takes effect immediately; remaining credit applies to the next cycle.',
  },
  {
    q: 'What payment methods are supported?',
    a: 'Crypto payment is available. Card payment is in preparation. All prices are in EUR.',
  },
  {
    q: 'Is MarketWatcher financial advice?',
    a: 'No. MarketWatcher provides market data, analysis tools, and AI-assisted insights — not individualized investment advice. Trading carries significant risk.',
  },
];

export const finalCta = {
  headline: ['Trade with the context', 'most traders never see.'],
  subtitle: 'Free account in 30 seconds — all basics available immediately.',
  primaryCta: 'Get started free',
  secondaryCta: 'View pricing',
  secondaryAnchor: '#pricing',
};

export const nav = {
  features: 'Features',
  pricing: 'Pricing',
  faq: 'FAQ',
};

export const common = {
  demo: 'DEMO',
  viewAllFeatures: 'View all features in detail →',
  viewAllFaq: 'View all questions →',
  viewPricingDetail: 'Full limits & feature comparison →',
  loadingPlans: 'Loading plans…',
  fallbackPlans: 'Plans from static snapshot — backend unreachable.',
};
